import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { LoanDefinitionType, SecurityType } from '@dealstack/shared/lib/generated';
import { useContext, useMemo } from 'react';
import { ManageInvestorDetailsContext } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestorDetails/InvestorDetails';
import { DATE_FORMATS, formatStringDateToUI } from '@dealstack/shared/lib/format/dates/date';
import { LONG_DASH } from '@dealstack/frontend/shared/other/react/specialSigns';
import { useMantineTableDataOperations } from '@dealstack/frontend/shared/components/Table/MantineTable/useMantineTableDataOperations';
import { getHoldingTableColumns } from './holdingTableColumns';
import { buildLoanRowName } from './holdingTable.utils';
import { HoldingOrLoan } from './holdingTable.types';

interface Props {
	getSecurityTypeTranslation: (type?: SecurityType | LoanDefinitionType) => string;
}

export const useHoldingTable = ({ getSecurityTypeTranslation }: Props) => {
	const tMipManage = useTranslationMipManage();
	const dataOperations = useMantineTableDataOperations();
	const { investorValuationsData, valuationsLoading, investorId } = useContext(
		ManageInvestorDetailsContext
	);

	const data = useMemo<HoldingOrLoan[]>(() => {
		const valuationsList: HoldingOrLoan[] = [...(investorValuationsData?.valuations ?? [])];

		valuationsList.push(
			...(investorValuationsData?.loans.map((loan) => ({
				type: loan.type,
				name: buildLoanRowName({
					name: loan.name,
					funder: loan.funder,
					issuerName: loan.issuerName,
					isViaVehicle: loan.isViaVehicle,
					heldByLabel: tMipManage('investors.details.holdings_table.held_by'),
					viaLabel: (issuer) =>
						tMipManage('investors.details.holdings_table.via_borrower', { issuer }),
				}),
				volume: tMipManage('investors.details.holdings_table.na'),
				value: loan.totalValue,
				cost: loan.totalValue,
			})) ?? [])
		);

		return valuationsList;
	}, [investorValuationsData, tMipManage]);

	const formattedValuationDate =
		formatStringDateToUI(
			investorValuationsData?.securityValuationDate,
			DATE_FORMATS.CALENDAR_DATE
		) ?? LONG_DASH;

	const columns = useMemo(
		() =>
			getHoldingTableColumns({
				tMipManage,
				currency: investorValuationsData?.currency,
				formattedValuationDate,
				getSecurityTypeTranslation,
			}),
		[
			tMipManage,
			investorValuationsData?.currency,
			formattedValuationDate,
			getSecurityTypeTranslation,
		]
	);

	return {
		data,
		columns,
		valuationsLoading,
		investorId,
		tMipManage,
		dataOperations,
	};
};
